import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { api, ApiError, getToken, setToken } from "@/lib/api";
import { queryClient } from "@/lib/query-client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [user, setUserState] = useState(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setStatus("guest");
      return;
    }
    try {
      const me = await api("/auth/me");
      setUserState(me);
      setStatus("authed");
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setToken(null);
        setUserState(null);
        setStatus("guest");
      } else {
        setStatus((s) => (s === "authed" ? s : "guest"));
      }
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const signIn = useCallback(async (token, u) => {
    setToken(token);
    queryClient.clear();
    setUserState(u);
    setStatus("authed");
  }, []);

  const signOut = useCallback(async () => {
    try { await api("/auth/logout", { method: "POST" }); } catch { /* token may already be invalid */ }
    setToken(null);
    queryClient.clear();
    setUserState(null);
    setStatus("guest");
  }, []);

  const value = useMemo(() => ({ status, user, signIn, signOut, setUser: setUserState, refresh }), [status, user, signIn, signOut, refresh]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
